import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { Coachmark, anchorProblem, prepareAnchor, useReducedMotion } from '@bookora/ui';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { useSessionStore } from '@/auth/token-store';
import { toast } from '@/lib/feedback';

import { useRecordGuidance } from './api';
import { useStepContext } from './context';
import { useTourFinish } from './finish';
import { headerInsets, useOverlayOpen } from './quiet';
import { resolveSteps, trimToMax, type ResolvedStep } from './resolve-steps';
import { RUNTIME_COPY } from './runtime-copy';
import { writeCoreSeen } from './storage';
import { reportProgress } from './struggle/store';
import {
  ANCHOR_RECHECK_SETTLE_MS,
  ANCHOR_RECHECK_WAIT_MS,
  OVERLAY_PAUSE_MAX_MS,
  SCROLL_SETTLE_MAX_MS,
} from './timing';
import { useTourStore, type ActiveTour } from './tour-store';
import { waitForAnchors } from './wait-anchors';
import { COPY } from './tours/copy';
import { TOURS } from './tours/registry';
import type { TourDef } from './tours/types';

/**
 * Runs floating/sheet tours (shell and page tours) and is the ONLY emitter of
 * their events — including the one `INTERRUPTED` on a route change. A chip
 * unmounting clears readiness and sends nothing.
 *
 * A run is keyed by `runId`: a new start mounts a fresh session, and a clinic
 * switch (which clears `active` synchronously) unmounts it with nothing sent.
 *
 * Steps are resolved UNTRIMMED, their anchors awaited, and only the live ones
 * are cut to `maxSteps` by the tour's priority (critic C10) — «≤ 5» counts the
 * steps shown. A deep link's start step survives the cut.
 *
 * The last step's button is the tour's `finish` when it has one for this
 * viewer (critic C7): COMPLETED is recorded, the run ends, then the panel
 * navigates. A route change mid-tour says once where to pick it up again.
 */
export function TourRunner(): JSX.Element | null {
  const active = useTourStore((s) => s.active);
  if (!active) return null;
  const tour = TOURS[active.key];
  if (!tour || tour.surface === 'inline') return null;
  return <RunnerSession key={active.runId} active={active} tour={tour} />;
}

/** Resolves after `ms`, or at once when `signal` aborts. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = (): void => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done);
  });
}

interface LiveStep {
  step: ResolvedStep;
  el: HTMLElement;
}

function RunnerSession({
  active,
  tour,
}: {
  active: ActiveTour;
  tour: TourDef;
}): JSX.Element | null {
  const record = useRecordGuidance();
  const ctx = useStepContext();
  const end = useTourStore((s) => s.end);
  const reduced = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const finishAction = useTourFinish(tour, ctx);

  const [steps, setSteps] = useState<LiveStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [preparedIndex, setPreparedIndex] = useState<number | null>(null);
  const endedRef = useRef(false);
  const startedRef = useRef(false);

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const stepsRef = useRef<LiveStep[] | null>(null);
  stepsRef.current = steps;
  const indexRef = useRef(index);
  indexRef.current = index;

  const emit = useCallback(
    (
      type: 'STARTED' | 'STEP_VIEWED' | 'STEP_SKIPPED' | 'COMPLETED' | 'DISMISSED' | 'INTERRUPTED',
      extra: {
        step?: number;
        stepId?: string;
        total?: number;
        reason?: 'anchor_missing' | 'anchor_lost' | 'route' | 'overlay' | 'anchors';
        durationMs?: number;
      } = {},
    ) => {
      record({
        type,
        kind: 'TOUR',
        key: tour.key,
        version: tour.version,
        source: active.source,
        ...extra,
      });
    },
    [record, tour.key, tour.version, active.source],
  );

  const finishRun = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    end();
  }, [end]);

  /* ── resolve: which steps, which anchors; again when the viewport crosses md ── */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    let resolved = resolveSteps(tour, ctxRef.current, { trim: false });
    const first = !startedRef.current;
    void (async () => {
      let found = await waitForAnchors(
        resolved.map((step) => step.anchor),
        { signal },
      );
      if (signal.aborted || endedRef.current) return;
      // A shell tour's anchors are the header and the nav, which every shell
      // route renders: one missing at the start means the shell was still
      // settling (a deep link on the first load after sign-in, a bell that
      // mounts late). Look once more after a short settle, re-resolving from
      // the CURRENT context in case the permissions arrived meanwhile, before
      // recording anything as skipped.
      if (first && tour.surface === 'shell' && found.some((el) => el === null)) {
        await pause(ANCHOR_RECHECK_SETTLE_MS, signal);
        if (signal.aborted || endedRef.current) return;
        resolved = resolveSteps(tour, ctxRef.current, { trim: false });
        found = await waitForAnchors(
          resolved.map((step) => step.anchor),
          { signal, timeoutMs: ANCHOR_RECHECK_WAIT_MS, graceMs: null },
        );
        if (signal.aborted || endedRef.current) return;
      }
      const present: LiveStep[] = [];
      resolved.forEach((step, i) => {
        const el = found[i];
        if (el) present.push({ step, el });
        else if (first) emit('STEP_SKIPPED', { stepId: step.id, reason: 'anchor_missing' });
      });
      const live = trimToMax(tour, present, first ? active.startAtStepId : undefined);

      if (live.length === 0) {
        if (first) toast.info(COPY.toast.unavailable);
        else emit('INTERRUPTED', { step: indexRef.current, reason: 'anchors' });
        finishRun();
        return;
      }

      let next = 0;
      if (first) {
        const at = active.startAtStepId
          ? live.findIndex((s) => s.step.id === active.startAtStepId)
          : -1;
        if (at >= 0) next = at;
        else if (active.resumeIndex !== undefined)
          next = Math.min(Math.max(0, active.resumeIndex), live.length - 1);
      } else {
        // Crossing md: keep the same step if it survives, else the next one.
        const previous = stepsRef.current ?? [];
        const currentId = previous[indexRef.current]?.step.id;
        const same = live.findIndex((s) => s.step.id === currentId);
        if (same >= 0) next = same;
        else {
          const laterIds = previous.slice(indexRef.current + 1).map((s) => s.step.id);
          const later = live.findIndex((s) => laterIds.includes(s.step.id));
          next = later >= 0 ? later : Math.min(indexRef.current, live.length - 1);
        }
      }
      setSteps(live);
      setIndex(next);
      setPreparedIndex(null);
      if (first) {
        startedRef.current = true;
        emit('STARTED', { step: next, stepId: live[next]?.step.id, total: live.length });
      }
    })();
    return () => controller.abort();
    // `ctx.viewport` is the only context change that re-resolves a running tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.viewport]);

  /* ── skip to the next usable step, or end the run ── */
  const skipLost = useCallback(
    (from: number) => {
      const live = stepsRef.current;
      if (!live || endedRef.current) return;
      emit('STEP_SKIPPED', {
        step: from,
        stepId: live[from]?.step.id,
        reason: 'anchor_lost',
        total: live.length,
      });
      for (let i = from + 1; i < live.length; i += 1) {
        const candidate = live[i];
        if (anchorProblem(candidate.el, { viewport: false }) === null) {
          setIndex(i);
          return;
        }
      }
      emit('INTERRUPTED', { step: from, reason: 'anchors', total: live.length });
      finishRun();
    },
    [emit, finishRun],
  );

  /* ── bring the current target into view before showing the step ── */
  useEffect(() => {
    if (!steps) return;
    const current = steps[index];
    if (!current) return;
    let cancelled = false;
    void prepareAnchor(current.el, { reducedMotion: reduced, maxMs: SCROLL_SETTLE_MAX_MS }).then(
      (ok) => {
        if (cancelled) return;
        if (ok || anchorProblem(current.el, { viewport: false }) === null) setPreparedIndex(index);
        else skipLost(index);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [steps, index, reduced, skipLost]);

  /* ── route change (push/pop of the PATHNAME) closes the tour ── */
  const startPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === startPath.current) return;
    if (navigationType === NavigationType.Replace) {
      startPath.current = location.pathname;
      return;
    }
    if (endedRef.current) return;
    const started = stepsRef.current !== null;
    emit('INTERRUPTED', {
      step: indexRef.current,
      stepId: stepsRef.current?.[indexRef.current]?.step.id,
      reason: 'route',
    });
    finishRun();
    // Once per run (the run ends here), and only for a tour the person saw.
    if (started) toast.info(RUNTIME_COPY.toast.interrupted);
  }, [location.pathname, navigationType, emit, finishRun]);

  /* ── a foreign overlay pauses the tour; one minute of it interrupts ── */
  const overlayOpen = useOverlayOpen(steps !== null);
  useEffect(() => {
    if (!overlayOpen) return;
    const timer = setTimeout(() => {
      if (endedRef.current) return;
      emit('INTERRUPTED', { step: indexRef.current, reason: 'overlay' });
      finishRun();
    }, OVERLAY_PAUSE_MAX_MS);
    return () => clearTimeout(timer);
  }, [overlayOpen, emit, finishRun]);

  const userId = useSessionStore((s) => s.user?.id ?? null);

  // The step on screen is the last one whose target is in view: while the
  // next target scrolls in, the previous step stays up (no flash, no focus
  // bouncing back to the launcher between steps).
  const shown = steps && preparedIndex !== null && steps[preparedIndex] ? preparedIndex : null;
  if (!steps || shown === null) return null;
  const current = steps[shown];
  const total = steps.length;

  return (
    <Coachmark
      open
      mode={ctx.viewport === 'compact' ? 'sheet' : 'floating'}
      hidden={overlayOpen}
      step={{
        id: current.step.id,
        title: current.step.title,
        body: current.step.body,
        target: current.el,
        placement: current.step.placement ?? 'auto',
        spotlightPadding: current.step.spotlightPadding,
      }}
      index={shown}
      total={total}
      labels={finishAction ? { finish: finishAction.label } : undefined}
      returnFocusTo={active.returnFocusTo}
      viewportInsets={headerInsets}
      onAnchorLost={() => skipLost(shown)}
      onNext={() => {
        const next = Math.min(shown + 1, total - 1);
        emit('STEP_VIEWED', { step: next, stepId: steps[next]?.step.id, total });
        setIndex(next);
      }}
      onPrev={() => {
        const prev = Math.max(0, shown - 1);
        emit('STEP_VIEWED', { step: prev, stepId: steps[prev]?.step.id, total });
        setIndex(prev);
      }}
      onFinish={() => {
        emit('COMPLETED', {
          step: shown,
          stepId: current.step.id,
          total,
          durationMs: Math.min(3_600_000, Math.max(0, Date.now() - active.startedAt)),
        });
        if (tour.key === 'core') writeCoreSeen(userId, true);
        reportProgress();
        finishRun();
        // After the run ended: `endedRef` keeps the route effect from
        // reading this navigation as an interruption.
        if (finishAction) void navigate(finishAction.to);
      }}
      onDismiss={() => {
        emit('DISMISSED', { step: shown, stepId: current.step.id, total });
        finishRun();
      }}
    />
  );
}
